import {
  type Article,
  type BaseMentionInfluencer,
  BroadcastMentionType,
  type CustomerArticle,
  type ExternalPlaybackMention,
  type InstagramPost,
  type LinkedinPost,
  type MagazineArticle,
  type Mention,
  type MentionOrCustomerArticle,
  MentionType,
  type PaperArticle,
  type PodcastEpisode,
  type ProgramAiring,
  type RadioClip,
  SocialMentionType,
  type Source,
  type TvCaption,
  type TvEyesMention,
  type TvLogoAppearance,
  type TvSuper,
} from "shared/types/mentions";

export function isMention(
  mention: MentionOrCustomerArticle
): mention is Mention {
  return "timestamp" in mention;
}

export function isCustomerArticle(
  mention: MentionOrCustomerArticle
): mention is CustomerArticle {
  return mention.type === MentionType.customer_article;
}

export function isTvCaption(mention: Mention): mention is TvCaption {
  return mention.type === MentionType.tv_caption;
}

export function isRadioClip(mention: Mention): mention is RadioClip {
  return mention.type === MentionType.radio_clip;
}

export function isPodcastEpisode(mention: Mention): mention is PodcastEpisode {
  return mention.type === MentionType.podcast_episode;
}

function isExternalPlaybackMention(
  mention: Mention
): mention is ExternalPlaybackMention {
  return (
    isPodcastEpisode(mention) || isTvCaption(mention) || isRadioClip(mention)
  );
}

export function isPaperArticle(mention: Mention): mention is PaperArticle {
  return mention.type === MentionType.paper_article;
}

export function isOnlineArticle(mention: Mention): mention is MagazineArticle {
  return mention.type === MentionType.article;
}

export function isMagazineArticle(
  mention: Mention
): mention is MagazineArticle {
  return mention.type === MentionType.magazine_article;
}

export function isTvEyesMention(mention: Mention): mention is TvEyesMention {
  const hasTvEyesPlayerUrl =
    "tveyes_slim_player_url" in mention &&
    Boolean(mention.tveyes_slim_player_url?.length);

  if (!isExternalPlaybackMention(mention)) return false;

  if (mention.broadcast_provider) {
    return mention.broadcast_provider.name === "tveyes";
  }

  return hasTvEyesPlayerUrl;
}

export function isKinetiqMention(
  mention: Mention
): mention is ExternalPlaybackMention {
  if (!isExternalPlaybackMention(mention)) return false;

  if (mention.broadcast_provider) {
    return mention.broadcast_provider.name === "kinetiq";
  }

  return (
    !isPodcastEpisode(mention) && mention.cision_source === "iqmedia-broadcast"
  );
}

export function isMentionWithAppRedirect(
  mention: Mention
): mention is Article | PaperArticle | MagazineArticle {
  return "in_app_redirect" in mention;
}

export function isMentionWithBody(
  mention: Mention
): mention is Article | PaperArticle {
  return "body" in mention || "translated_body" in mention;
}

export function isMentionWithDatahub(
  mention: Mention
): mention is Article | PaperArticle | RadioClip | TvCaption {
  return "datahub" in mention;
}

export function isMentionWithMediatrack(
  mention: Mention
): mention is PaperArticle | MagazineArticle {
  return "mediatrack_international_print" in mention;
}

export function isMentionRequiresAllowanceCheck(
  mention: Mention
): mention is PaperArticle | MagazineArticle {
  return "requires_allowance_check" in mention;
}

export function isMentionWithSyndications(
  mention: Mention
): mention is Extract<
  Mention,
  Article | PaperArticle | RadioClip | TvCaption | InstagramPost | LinkedinPost
> {
  return "syndications" in mention;
}

export function isMentionWithRelatedArticle(
  mention: Mention
): mention is Mention & {
  related_article: Article | PaperArticle;
} {
  return "related_article" in mention && Boolean(mention.related_article);
}

export function isMentionWithLicense(
  mention: Mention
): mention is Article | PaperArticle | MagazineArticle {
  return "license" in mention;
}

export function isMentionFinancialTimes(mention: Mention) {
  return (
    ("license" in mention && mention.license === "financial-times") ||
    mention.source?.name === "Financial Times"
  );
}

export function isMentionOutsideSourceRetentionPeriod(
  mention: Mention
): mention is Article | PaperArticle | MagazineArticle {
  return "outside_source_retention_period" in mention;
}

export function isTvMention(
  mention: Mention
): mention is TvCaption | TvSuper | TvLogoAppearance {
  return [
    MentionType.tv_caption,
    MentionType.tv_super,
    MentionType.tv_logo_appearance,
    MentionType.tv_story,
  ].includes(mention.type);
}

export function isRadioMention(mention: Mention): mention is RadioClip {
  return [MentionType.radio_clip, MentionType.radio_story].includes(
    mention.type
  );
}

export function isTvOrRadioMention(
  mention: Mention
): mention is RadioClip | TvCaption | TvLogoAppearance | TvSuper {
  return mention.type in BroadcastMentionType;
}

export function isSocialMentionType(
  field: MentionType | SocialMentionType
): field is SocialMentionType {
  return field in SocialMentionType;
}

export function isSocialMention(
  mention: Mention
): mention is Mention & { type: SocialMentionType } {
  return mention.type in SocialMentionType;
}

export function isInstagramPost(
  mention: Mention
): mention is Mention & { type: MentionType.instagram_post } {
  return mention.type === MentionType.instagram_post;
}

export function mentionHasUrl(
  mention: Mention
): mention is Extract<Mention, { url: string }> {
  return "url" in mention && typeof mention.url === "string";
}

export function isArticle(
  mention: Mention
): mention is Extract<Article, Article | MagazineArticle | PaperArticle> {
  return [
    MentionType.article,
    MentionType.magazine_article,
    MentionType.paper_article,
  ].includes(mention.type);
}

export function isTranscriptRequestMention(
  mention: Mention
): mention is Extract<
  Mention,
  TvCaption | TvSuper | RadioClip | PodcastEpisode
> {
  return [
    MentionType.tv_caption,
    MentionType.tv_super,
    MentionType.radio_clip,
    MentionType.podcast_episode,
  ].includes(mention.type);
}

export function isMentionWithSentiment(mention: Mention): mention is Mention {
  return "sentiment" in mention;
}

export function isMentionWithOutlet(
  sourceOrInfluencer: BaseMentionInfluencer | Source
): sourceOrInfluencer is Source {
  return "outlet_id" in sourceOrInfluencer;
}

export function isMentionWithInfluencer(
  sourceOrInfluencer: BaseMentionInfluencer | Source
): sourceOrInfluencer is BaseMentionInfluencer {
  return "id" in sourceOrInfluencer && "opted_type" in sourceOrInfluencer;
}

export function hasProgramAiring(
  mention: Mention
): mention is Mention & { program_airing: ProgramAiring } {
  return "program_airing" in mention && mention.program_airing !== undefined;
}

export function hasSourceId(
  mention: Mention
): mention is Mention & { source: { id: number } } {
  return "source" in mention && "id" in mention.source;
}

export function hasAuthor(
  mention: Mention
): mention is Mention & { author: string } {
  return "author" in mention && typeof mention.author === "string";
}

export function hasPublicLink(
  mention: Mention
): mention is Mention & { public_link: string } {
  return "public_link" in mention && typeof mention.public_link === "string";
}

export function hasInternalLink(
  mention: Mention
): mention is Mention & { internal_link: string } {
  return (
    "internal_link" in mention && typeof mention.internal_link === "string"
  );
}

export function hasTitle(
  mention: Mention
): mention is Mention & { title: string } {
  return "title" in mention && typeof mention.title === "string";
}

export function hasSourceName(
  mention: Mention
): mention is Mention & { source: { name: string } } {
  return "source" in mention && "name" in mention.source;
}

export function hasText(
  mention: Mention
): mention is Mention & { text: string } {
  return "text" in mention && typeof mention.text === "string";
}
